import React, { useEffect, useState } from "react";
import { Form, Button, Table, Radio, Image } from "antd";
import Banner from "../../../../views/components/sections/Banner";
import OverviewSection from "./OverviewSection";
import {
  useListService,
  useServiceDetail,
  useServiceSave,
} from "../../../../hooks/api/service/hooks";
import {
  IHighlights,
  IServiceList,
  IServicePayload,
} from "../../../../types/api/service";
import { getSingleImageUrl } from "../../../../lib/utils";
import MyPagination from "../../../../views/components/Pagination";
import TabsContain from "../../../../views/components/reuseComp/TabsContain";
import { useMessageApi } from "../../../../provider/AntdNotification";
import { useQueryState } from "nuqs";
import WhyTrustUs from "./whyTrustus";
import Guide from "./guide";
import GetInTouch from "./GetInTouch";
import OurProcess from "./OurProcess";

interface Props {
  allowURLStateUpdate?: Boolean;
  setBreadcrumb: React.Dispatch<React.SetStateAction<string>>;
}

const ServiceTabsDetail: React.FC<Props> = ({
  allowURLStateUpdate = true,
  setBreadcrumb,
}) => {
  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };

  const [form] = Form.useForm();
  const message = useMessageApi();
  const [tab, setTab] = useQueryState("tab");
  const activeTab = tab || "Banner";

  const {
    data,
    refetchWithQuery: refetch,
    isFetching,
  } = useListService({
    page: 1,
    limit: 20,
    ...DEFAULT_NUQS_CONFIG,
  });

  const serviceList: IServiceList[] = data?.data || [];

  const [isEditing, setIsEditing] = useState(false);
  const [selectedService, setSelectedService] = useState<any | null>(null);
  const [isSelectionDone, setIsSelectionDone] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [reviewMode, setReviewMode] = useState<"list" | "add" | "edit">("list");
  const [editingReviewIndex, setEditingReviewIndex] = useState<number | null>(
    null,
  );

  const qData = data?.qData ?? { page: 1, limit: 20 };

  const { mutate: saveService, isPending } = useServiceSave({ qData });

  const [detailSlug, setDetailSlug] = useState<string | null>(null);

  const { data: serviceDetailData, isFetching: isDetailFetching } =
    useServiceDetail(detailSlug || "", {
      enabled: !!detailSlug,
    });

  useEffect(() => {
    if (serviceDetailData?.data) {
      setSelectedService(serviceDetailData.data);
      setIsSelectionDone(true);
    }
  }, [serviceDetailData]);

  const handleChangeTab = (section: string) => {
    setTab(section);
    setIsEditing(false);
    setBreadcrumb(section);
  };

  const handleNext = () => {
    if (!selectedService) {
      return message.warning("Please select a service before proceeding.");
    }

    setDetailSlug(selectedService.slug);
  };
  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const { createdAt, updatedAt, __v, key, ...rest } = selectedService;

      let payload: IServicePayload = { ...rest };

      if (activeTab === "Banner") {
        payload = {
          ...rest,
          banner: {
            ...rest.banner,
            ...values.banner,
            image:
              getSingleImageUrl(values?.banner?.image) || rest?.banner?.image,
          },
        };
      } else if (activeTab === "Overview") {
        const existingSections = rest?.overview?.sections || [];
        const newSections = values?.overview?.sections || [];

        let updatedSections = [...existingSections];

        if (reviewMode === "add") {
          updatedSections = [...existingSections, ...newSections];
        }

        if (reviewMode === "edit" && editingReviewIndex !== null) {
          updatedSections[editingReviewIndex] = newSections[0];
        }

        payload = {
          ...rest,
          overview: {
            ...rest.overview,
            ...values.overview,
            image:
              getSingleImageUrl(values?.overview?.image) ||
              rest?.overview?.image,
            sections: updatedSections,
          },
        };

        setEditingReviewIndex(null);
        setReviewMode("list");
      } else if (activeTab === "Our Process") {
        const existingSteps = rest?.our_process?.steps || [];
        const newSteps = values?.our_process?.steps || [];

        let updatedSteps = [...existingSteps];

        if (reviewMode === "add") {
          updatedSteps = [...existingSteps, ...newSteps];
        }

        if (reviewMode === "edit" && editingReviewIndex !== null) {
          updatedSteps[editingReviewIndex] = newSteps[0];
        }

        payload = {
          ...rest,
          our_process: {
            ...rest.our_process,
            ...values.our_process,
            steps: updatedSteps,
          },
        };
      } else if (activeTab === "Why Trust Us") {
        const existingHighlights = rest?.why_trust_us?.highlights || [];
        const newHighlights = values?.why_trust_us?.highlights || [];

        let updatedHighlights = [...existingHighlights];

        if (reviewMode === "add") {
          updatedHighlights = [...existingHighlights, ...newHighlights];
        }

        if (reviewMode === "edit" && editingReviewIndex !== null) {
          updatedHighlights[editingReviewIndex] = newHighlights[0];
        }

        payload = {
          ...rest,
          why_trust_us: {
            ...rest.why_trust_us,
            ...values.why_trust_us,
            image:
              getSingleImageUrl(values?.why_trust_us?.image) ||
              rest?.why_trust_us?.image,
            highlights: updatedHighlights,
          },
        };
      } else if (activeTab === "Guide") {
        const existingTips = rest?.guide?.tips || [];
        const newTips = values?.guide?.tips || [];

        let updatedTips = [...existingTips];

        if (reviewMode === "add") {
          updatedTips = [...existingTips, ...newTips];
        }

        if (reviewMode === "edit" && editingReviewIndex !== null) {
          updatedTips[editingReviewIndex] = newTips[0];
        }

        payload = {
          ...rest,
          guide: {
            ...rest.guide,
            ...values.guide,
            tips: updatedTips,
          },
        };
      } else if (activeTab === "Get In Touch") {
        payload = {
          ...rest,
          getInTouch: {
            ...rest.getInTouch,
            ...values.getInTouch,
          },
        };
      }

      saveService(payload, {
        onSuccess: (response: any) => {
          const updatedService = response?.data || payload;

          message.success("Service updated successfully");

          setSelectedService(updatedService);

          setIsEditing(false);
          setEditingIndex(null);
          setEditingReviewIndex(null);
          setReviewMode("list");
        },
        onError: (error: any) => {
          message.error(
            error?.response?.data?.message || "Failed to save service",
          );
        },
      });
    } catch (error) {
      console.log("❌ Validation failed:", error);
    }
  };

  const columns = [
    {
      title: "",
      align: "center" as const,
      className: "w-[40px] sm:w-[50px] md:w-[60px]",
      render: (_: any, record: any) => (
        <Radio
          checked={selectedService?._id === record._id}
          onChange={() => setSelectedService(record)}
        />
      ),
    },
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      align: "center" as const,
      className: "w-[50px] sm:w-[60px] md:w-[70px]",
      render: (v: string) => <Image src={v} width={40} height={40} />,
    },
    {
      title: "Name",
      dataIndex: "heading",
      key: "heading",
      className: "min-w-[80px] sm:min-w-[180px] md:min-w-[220px]",
      render: (text: string) => (
        <span className="font-medium text-gray-800 text-[12px] sm:text-[14px] md:text-[16px]">
          {text}
        </span>
      ),
    },
    {
      title: "",
      key: "action",
      align: "right" as const,
      className: "w-[50px] sm:w-[60px] md:w-[70px]",
      render: (_: any, record: any) =>
        selectedService?._id === record._id ? (
          <Button
            type="dashed"
            loading={isDetailFetching}
            className="bg-primary text-white sm:px-10"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
          >
            Next
          </Button>
        ) : null,
    },
  ];

  type SectionField = "Overview" | "Our Process" | "Why Trust Us" | "Guide";
  const handleDelete = (index: number, field: SectionField) => {
    if (!selectedService) return;
    let payload: IServicePayload = { ...selectedService };

    if (field === "Overview") {
      const overview = selectedService.overview;
      if (!overview) return;
      const updatedContent = overview.sections.filter(
        (_: IHighlights, i: number) => i !== index,
      );
      payload = {
        ...selectedService,
        overview: { ...overview, sections: updatedContent },
      };
    }

    if (field === "Our Process") {
      const ourProcess = selectedService.our_process;
      if (!ourProcess) return;

      const updatedContent = ourProcess.steps.filter(
        (_: any, i: number) => i !== index,
      );

      payload = {
        ...selectedService,
        our_process: { ...ourProcess, steps: updatedContent },
      };
    }

    if (field === "Why Trust Us") {
      const whyTrustUs = selectedService.why_trust_us;
      if (!whyTrustUs) return;
      const updatedContent = whyTrustUs.highlights.filter(
        (_: IHighlights, i: number) => i !== index,
      );
      payload = {
        ...selectedService,
        why_trust_us: { ...whyTrustUs, highlights: updatedContent },
      };
    }

    if (field === "Guide") {
      const Guide = selectedService.guide;
      if (!Guide) return;
      const updatedContent = Guide.tips.filter(
        (_: IHighlights, i: number) => i !== index,
      );
      payload = {
        ...selectedService,
        guide: { ...Guide, tips: updatedContent },
      };
    }


    saveService(payload, {
      onSuccess: () => {
        message.success("Data deleted successfully");
        setSelectedService(payload);
        setReviewMode("list");
        setEditingReviewIndex(null);
      },
      onError: () => message.error("Failed to delete"),
    });
  };

  const sections = [
    "Banner",
    "Overview",
    "Our Process",
    "Why Trust Us",
    "Guide",
    "Get In Touch",
  ];

  return (
    <div>
      {!isSelectionDone && (
        <div className="bg-white p-2 sm:p-4 rounded-md shadow-sm">
          <h2 className="text-lg font-semibold mb-3">
            Select a Service to Edit
          </h2>

          <Table
            dataSource={serviceList}
            columns={columns}
            rowKey={(record: any) => record._id}
            size="small"
            pagination={false}
            bordered
            className="custom-table-class"
            footer={() => (
              <MyPagination
                qData={qData}
                onChange={refetch}
                total={qData.total}
                DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
              />
            )}
            loading={isFetching}
            onRow={(record) => ({
              onClick: () => setSelectedService(record),
            })}
            rowClassName={(record) =>
              selectedService?._id === record._id
                ? "bg-green-100 hover:bg-green-200 cursor-pointer transition-all"
                : "hover:bg-gray-50 cursor-pointer"
            }
          />
        </div>
      )}

      {isSelectionDone && selectedService && (
        <>
          <div className="flex  mt-4 w-full">
            <Button
              type="dashed"
              color="default"
              className="mb-4 bg-primary text-white w-full"
              onClick={() => {
                setIsSelectionDone(false);
                setSelectedService(null);
              }}
            >
              Select Another Service
            </Button>
          </div>

          <h2 className="text-lg font-semibold mb-4">
            Editing Service:{" "}
            <span className="text-green-600">{selectedService.banner.heading}</span>
          </h2>

          <TabsContain
            handleChangeTab={handleChangeTab}
            sections={sections}
            activeTab={activeTab}
          />

          {activeTab === "Banner" && (
            <Banner
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              bannerData={selectedService?.banner}
              loading={isPending}
            />
          )}

          {activeTab === "Overview" && (
            <OverviewSection
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              overviewData={selectedService?.overview}
              loading={isPending}
              onDelete={handleDelete}
              mode={reviewMode}
              setMode={setReviewMode}
              editingIndex={editingReviewIndex}
              setEditingIndex={setEditingReviewIndex}
            />
          )}

          {activeTab === "Our Process" && (
            <OurProcess
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              ourProcessData={selectedService?.our_process}
              loading={isPending}
              onDelete={handleDelete}
              mode={reviewMode}
              setMode={setReviewMode}
              editingIndex={editingReviewIndex}
              setEditingIndex={setEditingReviewIndex}
            />
          )}

          {activeTab === "Why Trust Us" && (
            <WhyTrustUs
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              whyTrustUsData={selectedService?.why_trust_us}
              loading={isPending}
              onDelete={handleDelete}
              mode={reviewMode}
              setMode={setReviewMode}
              editingIndex={editingReviewIndex}
              setEditingIndex={setEditingReviewIndex}
            />
          )}
          {activeTab === "Guide" && (
            <Guide
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              guideData={selectedService?.guide}
              loading={isPending}
              onDelete={handleDelete}
              mode={reviewMode}
              setMode={setReviewMode}
              editingIndex={editingReviewIndex}
              setEditingIndex={setEditingReviewIndex}
            />
          )}

          {activeTab === "Get In Touch" && (
            <GetInTouch
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              getInTouchData={selectedService?.getInTouch}
              loading={isPending}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ServiceTabsDetail;
